import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { saveScreen, saveCheckpoint, getAllScreens, getAllCheckpoints, isExternalUrl } from '../../services/funnelService';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.doryBeige};
  padding: ${theme.spacing.lg};
  overflow-y: auto;
  max-height: 100vh;
`;

const Header = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary' 
    ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)` 
    : props.variant === 'danger'
    ? theme.colors.error 
    : 'white'};
  color: ${props => props.variant === 'primary' || props.variant === 'danger' ? 'white' : theme.colors.text};
  border: ${props => props.variant === 'primary' || props.variant === 'danger' ? 'none' : `2px solid ${theme.colors.primary}`};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  
  @media screen and (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const SectionTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 2px solid ${theme.colors.doryLighterBlue};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
`;

const Input = styled.input`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const DestinationTypeRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const DestinationTypeOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.text};
  cursor: pointer;
`;

const HelpText = styled.p`
  margin: ${theme.spacing.xs} 0 0;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: #666;
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  min-height: 200px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const OptionGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
`;

const OptionInput = styled(Input)`
  flex: 1;
`;

const RemoveButton = styled.button`
  background: ${theme.colors.error};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  cursor: pointer;
  font-size: ${theme.typography.sizes.small};
  
  &:hover {
    opacity: 0.8;
  }
`;

const AddOptionButton = styled.button`
  background: ${theme.colors.doryBlue};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
  font-size: ${theme.typography.sizes.small};
  
  &:hover {
    opacity: 0.8;
  }
`;

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: ${theme.colors.error};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
`;

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #006600;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
`;



const DEFAULT_CONVERSION_CONFIG = {
  enabled: false,
  trigger: 'complete', // 'load' | 'complete'
  meta: { enabled: false, eventName: '', testEventCode: '' },
  linkedin: { enabled: false, conversionRuleUrn: '' },
  google: { enabled: false, conversionActionId: '', validateOnly: false },
  value: '',
  currency: 'USD',
  usePaymentAmount: false
};

const NextDestinationField = ({ label, value, onChange, screenOptions }) => {
  const [mode, setMode] = useState(() => (isExternalUrl(value) ? 'url' : 'screen'));

  useEffect(() => {
    if (isExternalUrl(value)) {
      setMode('url');
    } else if (value) {
      setMode('screen');
    }
  }, [value]);

  const switchToScreen = () => {
    setMode('screen');
    onChange('');
  };

  const switchToUrl = () => {
    setMode('url');
    if (!isExternalUrl(value)) {
      onChange('https://');
    }
  };

  return (
    <FormGroup>
      <Label>{label}</Label>
      <DestinationTypeRow>
        <DestinationTypeOption>
          <input
            type="radio"
            name={`dest-mode-${label}`}
            checked={mode === 'screen'}
            onChange={switchToScreen}
          />
          Screen / Checkpoint
        </DestinationTypeOption>
        <DestinationTypeOption>
          <input
            type="radio"
            name={`dest-mode-${label}`}
            checked={mode === 'url'}
            onChange={switchToUrl}
          />
          External URL
        </DestinationTypeOption>
      </DestinationTypeRow>
      {mode === 'url' ? (
        <>
          <Input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/path"
          />
          <HelpText>User will leave the funnel and open this URL.</HelpText>
        </>
      ) : (
        <Select
          value={isExternalUrl(value) ? '' : (value || '')}
          onChange={(e) => onChange(e.target.value)}
        >
          {screenOptions}
        </Select>
      )}
    </FormGroup>
  );
};

const ScreenEditor = ({ screen, type, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    key: '',
    type: type === 'screen' ? 'learning' : 'question',
    content: {
      // Initialize with default content values
      question: '',
      options: [],
      correctAnswer: 0,
      ctaText: 'Continue',
      showCta: true,
      ctaButtonType: 'next_screen_or_checkpoint',
      ctaButtonText: '',
      showFullName: false,
      fullNamePlaceholder: 'Enter your full name',
      emailPlaceholder: 'Enter your email address',
      phonePlaceholder: 'Enter your phone number (e.g., 555-123-4567)',
      defaultCountryCode: '+1',
      videoUrl: '',
      stripeProductId: '',
      stripeAmount: 100, // Default to $1.00 (100 cents)
      dollarAmount: 1.00, // Default to $1.00 USD
      linkUrl: '',
      text: '',
      images: [],
      journalPrompt: '',
      confirmationStatement: '',
      // Add missing fields to initial state
      activeCampaignTagId: '',
      productName: '',
      productImage: '',
      // Testimonial fields
      authorName: '',
      authorTitle: '',
      authorImage: '',
      startPace: '',
      endPace: '',
      // As Seen In fields
      brandImage: '',
      brandName: '',
      category: '',
      headline: '',
      subheading: '',
      articleUrl: '',
      // Media Appearance fields
      logoImage: '',
      logoAlt: '',
      // Community Members fields
      members: [],
      // Payment Membership fields
      membershipOptions: [],
      // Membership Details fields
      valueBoxes: [],
      studyBox: {
        title: '',
        description: '',
        icon: '',
        image: ''
      },
      // Open Graph Meta Tags for Learning screens
      ogMetaTags: {
        title: '',
        description: '',
        image: '',
        imageWidth: '',
        imageHeight: '',
        url: '',
        type: '',
        siteName: ''
      }
    },
    journalConfig: {
      collectsHours: true,
      collectsPressure: true,
      collectsEmotions: true,
      pressureQuestion: 'How would you rate the current level of pressure you\'re under?',
      hoursQuestion: 'How many hours did you work on your most recent full work day?',
      hoursWorkedType: 'yesterday', // 'yesterday' or 'lastWeek'
      pressureLabels: { low: 'No pressure', high: 'Extreme pressure' },
      hoursLabels: { low: '0 hours', high: '24 hours' },
      emotionalQuestions: {
        energy: 'How much energy are you feeling right now?',
        depression: 'How much satisfaction are you feeling right now?',
        happiness: 'How positive do you feel right now?',
        hours: 'How strong is your personal identity right now?',
        focus: 'How focused are you able to be right now?',
        mania: 'How steady is your mood right now?',
        calm: 'How calm are you feeling right now?'
      },
      emotionalLabels: {
        energy: { low: 'Empty', high: 'Full' },
        depression: { low: 'Depressed', high: 'Satisfied' },
        happiness: { low: 'Unhappy', high: 'Positive' },
        hours: { low: 'Weak', high: 'Strong' },
        focus: { low: 'Distracted', high: 'Focused' },
        mania: { low: 'Manic', high: 'In Control' },
        calm: { low: 'Anxious', high: 'Calm' }
      }
    },
    nextScreen: '',
    isActive: true,
    required: true,
    collectsUserData: [],
    conversionConfig: { ...DEFAULT_CONVERSION_CONFIG }
  });

  const [availableScreens, setAvailableScreens] = useState([]);
  const [availableCheckpoints, setAvailableCheckpoints] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOgSectionExpanded, setIsOgSectionExpanded] = useState(false);

  useEffect(() => {
    loadAvailableItems();
    if (screen) {
      // Check if Open Graph tags exist and auto-expand section if they do
      const hasOgTags = screen.content?.ogMetaTags && 
        Object.values(screen.content.ogMetaTags).some(value => 
          value && String(value).trim() !== ''
        );
      if (hasOgTags) {
        setIsOgSectionExpanded(true);
      }

      // Merge the existing screen data with default journalConfig values
      // Explicitly preserve the id field to ensure updates work correctly
      const mergedScreen = {
        id: screen?.id, // Explicitly preserve the ID
        ...screen,
        content: {
          // Apply existing screen content or defaults for missing fields
          question: screen.content?.question ?? '',
          options: screen.content?.options ?? [],
          correctAnswer: screen.content?.correctAnswer ?? 0,
          ctaText: screen.content?.ctaText ?? (screen.type === 'payment_membership' ? 'Subscribe Now' : 'Continue'),
          showCta: screen.content?.showCta ?? true,
          ctaButtonType: screen.content?.ctaButtonType ?? (screen.content?.showCta === false ? 'no_button' : 'next_screen_or_checkpoint'),
          ctaButtonText: screen.content?.ctaButtonText ?? '',
          showFullName: screen.content?.showFullName ?? false,
          fullNamePlaceholder: screen.content?.fullNamePlaceholder ?? 'Enter your full name',
          emailPlaceholder: screen.content?.emailPlaceholder ?? 'Enter your email address',
          phonePlaceholder: screen.content?.phonePlaceholder ?? 'Enter your phone number (e.g., 555-123-4567)',
          defaultCountryCode: screen.content?.defaultCountryCode ?? '+1',
          stripeProductId: screen.content?.stripeProductId ?? '',
          stripeAmount: screen.content?.stripeAmount ?? 100, // Default to $1.00 (100 cents)
          dollarAmount: screen.content?.dollarAmount ?? 1.00, // Default to $1.00 USD
          linkUrl: screen.content?.linkUrl ?? '',
          text: screen.content?.text ?? '',
          images: screen.content?.images ?? [],
          journalPrompt: screen.content?.journalPrompt ?? '',
          confirmationStatement: screen.content?.confirmationStatement ?? '',
          // Add missing ActiveCampaign Tag ID field
          activeCampaignTagId: screen.content?.activeCampaignTagId ?? '',
          // Add missing product name field
          productName: screen.content?.productName ?? '',
          productImage: screen.content?.productImage ?? '',
          // Testimonial fields
          authorName: screen.content?.authorName ?? '',
          authorTitle: screen.content?.authorTitle ?? '',
          authorImage: screen.content?.authorImage ?? '',
          startPace: screen.content?.startPace ?? '',
          endPace: screen.content?.endPace ?? '',
          // As Seen In fields
          brandImage: screen.content?.brandImage ?? '',
          brandName: screen.content?.brandName ?? '',
          category: screen.content?.category ?? '',
          headline: screen.content?.headline ?? '',
          subheading: screen.content?.subheading ?? '',
          articleUrl: screen.content?.articleUrl ?? '',
          // Media Appearance fields
          logoImage: screen.content?.logoImage ?? '',
          logoAlt: screen.content?.logoAlt ?? '',
          // Community Members fields
          members: screen.content?.members ?? [],
          // Membership Details fields
          valueBoxes: screen.content?.valueBoxes ?? [],
          studyBox: screen.content?.studyBox ?? {
            title: '',
            description: '',
            icon: '',
            image: ''
          },
          // Open Graph Meta Tags
          ogMetaTags: screen.content?.ogMetaTags ?? {
            title: '',
            description: '',
            image: '',
            imageWidth: '',
            imageHeight: '',
            url: '',
            type: '',
            siteName: ''
          },
          // Preserve any other content fields that might exist
          ...(screen.content || {})
        },
        journalConfig: {
          collectsHours: true,
          collectsPressure: true,
          collectsEmotions: true,
          pressureQuestion: 'How would you rate the current level of pressure you\'re under?',
          hoursQuestion: 'How many hours did you work on your most recent full work day?',
          pressureLabels: { low: 'No pressure', high: 'Extreme pressure' },
          hoursLabels: { low: '0 hours', high: '24 hours' },
          emotionalQuestions: {
            energy: 'How much energy are you feeling right now?',
            depression: 'How much satisfaction are you feeling right now?',
            happiness: 'How positive do you feel right now?',
            hours: 'How strong is your personal identity right now?',
            focus: 'How focused are you able to be right now?',
            mania: 'How steady is your mood right now?',
            calm: 'How calm are you feeling right now?'
          },
          emotionalLabels: {
            energy: { low: 'Empty', high: 'Full' },
            depression: { low: 'Depressed', high: 'Satisfied' },
            happiness: { low: 'Unhappy', high: 'Positive' },
            hours: { low: 'Weak', high: 'Strong' },
            focus: { low: 'Distracted', high: 'Focused' },
            mania: { low: 'Manic', high: 'In Control' },
            calm: { low: 'Anxious', high: 'Calm' }
          },
          ...(screen.journalConfig || {})
        },
        conversionConfig: {
          ...DEFAULT_CONVERSION_CONFIG,
          ...(screen.conversionConfig || {}),
          meta: {
            ...DEFAULT_CONVERSION_CONFIG.meta,
            ...(screen.conversionConfig?.meta || {})
          },
          linkedin: {
            ...DEFAULT_CONVERSION_CONFIG.linkedin,
            ...(screen.conversionConfig?.linkedin || {})
          },
          google: {
            ...DEFAULT_CONVERSION_CONFIG.google,
            ...(screen.conversionConfig?.google || {})
          }
        }
      };
      
      setFormData(mergedScreen);
    }
  }, [screen]);

  const loadAvailableItems = async () => {
    try {
      const [screens, checkpoints] = await Promise.all([
        getAllScreens(),
        getAllCheckpoints()
      ]);
      const byKey = (a, b) => (a.key || '').localeCompare(b.key || '');
      setAvailableScreens(Object.values(screens).sort(byKey));
      setAvailableCheckpoints(Object.values(checkpoints).sort(byKey));
    } catch (error) {
      console.error('Error loading available items:', error);
    }
  };

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData(prev => {
        const newData = {
          ...prev,
          [nested]: {
            ...prev[nested],
            [field]: value
          }
        };
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [field]: value
        };
        return newData;
      });
    }
    setError('');
    setSuccess('');
  };

  const handleConversionConfigChange = (path, value) => {
    setFormData(prev => {
      const nextConfig = {
        ...DEFAULT_CONVERSION_CONFIG,
        ...prev.conversionConfig,
        meta: { ...DEFAULT_CONVERSION_CONFIG.meta, ...prev.conversionConfig?.meta },
        linkedin: { ...DEFAULT_CONVERSION_CONFIG.linkedin, ...prev.conversionConfig?.linkedin },
        google: { ...DEFAULT_CONVERSION_CONFIG.google, ...prev.conversionConfig?.google }
      };

      const parts = path.split('.');
      let target = nextConfig;
      for (let i = 0; i < parts.length - 1; i += 1) {
        target[parts[i]] = { ...target[parts[i]] };
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;

      return { ...prev, conversionConfig: nextConfig };
    });
    setError('');
    setSuccess('');
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.content.options];
    newOptions[index] = value;
    handleInputChange('options', newOptions, 'content');
  };

  const addOption = () => {
    const newOptions = [...formData.content.options, ''];
    handleInputChange('options', newOptions, 'content');
  };

  const removeOption = (index) => {
    const newOptions = formData.content.options.filter((_, i) => i !== index);
    handleInputChange('options', newOptions, 'content');
  };

  const handleYesNoNavigation = (answerType, value) => {
    const currentNextScreen =
      formData.nextScreen && typeof formData.nextScreen === 'object'
        ? formData.nextScreen
        : {
            conditions: {},
            default:
              typeof formData.nextScreen === 'string' ? formData.nextScreen : '',
          };
    
    if (answerType === 'yes') {
      handleInputChange('nextScreen', {
        ...currentNextScreen,
        conditions: {
          ...currentNextScreen.conditions,
          1: value // 1 = Yes
        }
      });
    } else if (answerType === 'no') {
      handleInputChange('nextScreen', {
        ...currentNextScreen,
        conditions: {
          ...currentNextScreen.conditions,
          0: value // 0 = No
        }
      });
    } else if (answerType === 'default') {
      handleInputChange('nextScreen', {
        ...currentNextScreen,
        default: value
      });
    }
  };

  const handleJournalConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      journalConfig: {
        ...prev.journalConfig,
        [field]: value
      }
    }));
    setError('');
    setSuccess('');
  };





  const validateForm = () => {
    if (!formData.key.trim()) {
      setError('Key is required');
      return false;
    }

    if (type === 'checkpoint' && ['question', 'quiz'].includes(formData.type)) {
      if (!formData.content.question.trim()) {
        setError('Question is required for question/quiz checkpoints');
        return false;
      }
      
      if (formData.content.options.length < 2) {
        setError('At least 2 options are required for question/quiz checkpoints');
        return false;
      }
      
      if (formData.content.options.some(opt => !opt.trim())) {
        setError('All options must have text');
        return false;
      }
    }

    if (type === 'checkpoint' && formData.type === 'yes_no') {
      if (!formData.content.question.trim()) {
        setError('Question is required for yes/no checkpoints');
        return false;
      }
    }

    const destinationsToValidate = [];
    if (
      type === 'checkpoint' &&
      (formData.type === 'yes_no' || formData.type === 'commitment') &&
      formData.nextScreen &&
      typeof formData.nextScreen === 'object'
    ) {
      destinationsToValidate.push(
        formData.nextScreen.conditions?.[1],
        formData.nextScreen.conditions?.[0],
        formData.nextScreen.default
      );
    } else if (typeof formData.nextScreen === 'string') {
      destinationsToValidate.push(formData.nextScreen);
    }

    for (const dest of destinationsToValidate) {
      if (!dest || !isExternalUrl(dest)) continue;
      try {
        const parsed = new URL(dest);
        if (!parsed.hostname) {
          setError('External URL next steps must be a complete URL (e.g. https://example.com)');
          return false;
        }
      } catch {
        setError('External URL next steps must be a valid URL (e.g. https://example.com)');
        return false;
      }
    }

    return true;
  };

  const handleSaveOnly = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (type === 'screen') {
        const result = await saveScreen(formData);
        
        // Update formData with the returned ID so subsequent saves update instead of create
        if (result && result.id) {
          setFormData(prev => ({
            ...prev,
            id: result.id
          }));
        }
      } else {
        await saveCheckpoint(formData);
      }
      
      setSuccess(`${type === 'screen' ? 'Screen' : 'Checkpoint'} saved successfully!`);
      // Don't close the editor, just show success message
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setError(`Failed to save ${type}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (type === 'screen') {
        const result = await saveScreen(formData);
        
        // Update formData with the returned ID so subsequent saves update instead of create
        if (result && result.id) {
          setFormData(prev => ({
            ...prev,
            id: result.id
          }));
        }
      } else {
        await saveCheckpoint(formData);
      }
      
      setSuccess(`${type === 'screen' ? 'Screen' : 'Checkpoint'} saved successfully!`);
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (err) {
      console.error('Error saving:', err);
      setError(`Failed to save ${type}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderBasicFields = () => (
    <FormSection>
      <SectionTitle>Basic Information</SectionTitle>
      
      <FormGroup>
        <Label>Key *</Label>
        <Input
          type="text"
          value={formData.key}
          onChange={(e) => handleInputChange('key', e.target.value)}
          placeholder="unique-key-name"
        />
      </FormGroup>

      <FormGroup>
        <Label>Type *</Label>
        <Select
          value={formData.type}
          onChange={(e) => handleInputChange('type', e.target.value)}
        >
          {type === 'screen' ? (
            <>
              <option value="learning">Learning</option>
              <option value="journal_entry">Journal Entry</option>
              <option value="optimal_pace">Optimal Pace</option>
              <option value="optimal_pace_race">Optimal Pace Race</option>
              <option value="optimal_pace_solo">Optimal Pace Solo</option>
              <option value="optimal_pace_isolated">Optimal Pace Isolated</option>
              <option value="optimal_pace_learning">Optimal Pace Learning</option>
              <option value="membership_selection">Membership Selection</option>
              <option value="mental_resilience">Mental Resilience</option>
              <option value="safe_sprinting">Safe Sprinting</option>
              <option value="testimonial">Testimonial</option>
              <option value="community_members">Community Members</option>
              <option value="membership_details">Membership Details</option>
              <option value="membership_summary">Membership Summary</option>
              <option value="study_details">Study Details</option>
              <option value="as_seen_in">As Seen In</option>
              <option value="media_appearance">Media Appearance</option>
              <option value="leaderboard">Leaderboard</option>
              <option value="membership_entry">Membership Entry</option>
              <option value="dashboard">Dashboard</option>
              <option value="sprint_recovery_protocol">Sprint Recovery Protocol</option>
              <option value="user_info">User Info</option>
            </>
          ) : (
            <>
              <option value="question">Question</option>
              <option value="quiz">Quiz</option>
              <option value="yes_no">Yes/No Question</option>
              <option value="email_capture">Email Capture</option>
              <option value="phone_verification">Phone Verification</option>
              <option value="payment">Payment</option>
              <option value="payment_simple">Payment Simple</option>
              <option value="payment_membership">Payment Membership</option>
              <option value="marketing_landing_page">Marketing Landing Page</option>
              <option value="link">Link</option>
              <option value="journal_entry">Journal Entry</option>
              <option value="goals">Goals</option>
              <option value="speedbumps">Speedbumps</option>
              <option value="which_are_you">Which Are You</option>
              <option value="first_name">First Name</option>
              <option value="commitment">Commitment</option>
            </>
          )}
        </Select>
      </FormGroup>
    </FormSection>
  );

  const renderContentFields = () => (
    <FormSection>
      <SectionTitle>Content</SectionTitle>
      
      {/* Text content - available for all types */}
      <FormGroup>
        <Label>Text Content</Label>
        

        
        <TextArea
          value={formData.content.text}
          onChange={(e) => handleInputChange('text', e.target.value, 'content')}
          placeholder={type === 'screen' 
            ? 'Main content text (supports basic HTML like <strong>bold</strong>, <a href="https://...">links</a>, and newlines for line breaks)' 
            : 'Explanatory text or instructions (supports basic HTML like <strong>bold</strong>, <a href="https://...">links</a>, and newlines)'}
        />
      </FormGroup>

      {/* CTA Button Text - for screens and checkpoints (but not payment_membership which has its own field, or optimal_pace_isolated which has no CTA) */}
      {(type === 'screen' || (type === 'checkpoint' && formData.type !== 'payment_membership')) && formData.type !== 'optimal_pace_isolated' && (
        <FormGroup>
          <Label>CTA Button Text</Label>
          <Input
            type="text"
            value={formData.content.ctaText}
            onChange={(e) => handleInputChange('ctaText', e.target.value, 'content')}
            placeholder="Continue"
          />
        </FormGroup>
      )}

      {/* CTA Button Type - for optimal_pace_solo screens */}
      {formData.type === 'optimal_pace_solo' && (
        <>
          <FormGroup>
            <Label>CTA Button Type</Label>
            <Select
              value={formData.content?.ctaButtonType || (formData.content?.showCta === false ? 'no_button' : 'next_screen_or_checkpoint')}
              onChange={(e) => handleInputChange('ctaButtonType', e.target.value, 'content')}
            >
              <option value="next_screen_or_checkpoint">Next Screen or Checkpoint</option>
              <option value="no_button">No CTA Button</option>
            </Select>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Select whether to show a CTA button that navigates to the next screen/checkpoint, or hide the button entirely.
            </div>
          </FormGroup>
          
          {/* Button Text - shown when CTA button type is not "no_button" */}
          {(() => {
            const currentButtonType = formData.content?.ctaButtonType || 
              (formData.content?.showCta === false ? 'no_button' : 'next_screen_or_checkpoint');
            return currentButtonType !== 'no_button';
          })() && (
            <FormGroup>
              <Label>Button Text</Label>
              <Input
                type="text"
                value={formData.content?.ctaButtonText || ''}
                onChange={(e) => handleInputChange('ctaButtonText', e.target.value, 'content')}
                placeholder="Next"
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px', 
                fontStyle: 'italic' 
              }}>
                Custom text for the CTA button. Defaults to "Next" if left empty.
              </div>
            </FormGroup>
          )}
        </>
      )}

      {/* Journal Entry Configuration */}
      {formData.type === 'journal_entry' && (
        <>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.journalConfig?.collectsHours || false}
                onChange={(e) => handleJournalConfigChange('collectsHours', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Include Hours Worked Question
            </Label>
          </FormGroup>

          <FormGroup>
            <Label>Pressure Question</Label>
            

            
            <Input
              type="text"
              value={formData.journalConfig?.pressureQuestion || ''}
              onChange={(e) => handleJournalConfigChange('pressureQuestion', e.target.value)}
              placeholder="How would you rate the current level of pressure you're under?"
            />
          </FormGroup>

          {formData.journalConfig?.collectsHours && (
            <>
              <FormGroup>
                <Label>Hours Worked Question</Label>
                

                
                <Input
                  type="text"
                  value={formData.journalConfig?.hoursQuestion || ''}
                  onChange={(e) => handleJournalConfigChange('hoursQuestion', e.target.value)}
                  placeholder="How many hours did you work on your most recent full work day?"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Hours Worked Type</Label>
                <Select
                  value={formData.journalConfig?.hoursWorkedType || 'yesterday'}
                  onChange={(e) => handleJournalConfigChange('hoursWorkedType', e.target.value)}
                >
                  <option value="yesterday">Hours from Yesterday (0-24 hour scale)</option>
                  <option value="lastWeek">Hours from Last Week (range selection)</option>
                </Select>
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label>Pressure Scale Labels</Label>
            <OptionsContainer>
              <OptionGroup>
                <OptionInput
                  type="text"
                  value={formData.journalConfig?.pressureLabels?.low || ''}
                  onChange={(e) => {
                    const newPressureLabels = {
                      ...formData.journalConfig?.pressureLabels,
                      low: e.target.value
                    };
                    handleJournalConfigChange('pressureLabels', newPressureLabels);
                  }}
                  placeholder="Low label"
                />
                <span style={{ 
                  fontSize: theme.typography.sizes.small, 
                  color: theme.colors.textLight,
                  minWidth: '120px',
                  textAlign: 'right'
                }}>
                  Low
                </span>
              </OptionGroup>
              <OptionGroup>
                <OptionInput
                  type="text"
                  value={formData.journalConfig?.pressureLabels?.high || ''}
                  onChange={(e) => {
                    const newPressureLabels = {
                      ...formData.journalConfig?.pressureLabels,
                      high: e.target.value
                    };
                    handleJournalConfigChange('pressureLabels', newPressureLabels);
                  }}
                  placeholder="High label"
                />
                <span style={{ 
                  fontSize: theme.typography.sizes.small, 
                  color: theme.colors.textLight,
                  minWidth: '120px',
                  textAlign: 'right'
                }}>
                  High
                </span>
              </OptionGroup>
            </OptionsContainer>
          </FormGroup>

          {formData.journalConfig?.collectsHours && (
            <FormGroup>
              <Label>Hours Scale Labels</Label>
              <OptionsContainer>
                <OptionGroup>
                  <OptionInput
                    type="text"
                    value={formData.journalConfig?.hoursLabels?.low || ''}
                    onChange={(e) => {
                      const newHoursLabels = {
                        ...formData.journalConfig?.hoursLabels,
                        low: e.target.value
                      };
                      handleJournalConfigChange('hoursLabels', newHoursLabels);
                    }}
                    placeholder="Low label"
                  />
                  <span style={{ 
                    fontSize: theme.typography.sizes.small, 
                    color: theme.colors.textLight,
                    minWidth: '120px',
                    textAlign: 'right'
                  }}>
                    Low
                  </span>
                </OptionGroup>
                <OptionGroup>
                  <OptionInput
                    type="text"
                    value={formData.journalConfig?.hoursLabels?.high || ''}
                    onChange={(e) => {
                      const newHoursLabels = {
                        ...formData.journalConfig?.hoursLabels,
                        high: e.target.value
                      };
                      handleJournalConfigChange('hoursLabels', newHoursLabels);
                    }}
                    placeholder="High label"
                  />
                  <span style={{ 
                    fontSize: theme.typography.sizes.small, 
                    color: theme.colors.textLight,
                    minWidth: '120px',
                    textAlign: 'right'
                  }}>
                    High
                  </span>
                </OptionGroup>
              </OptionsContainer>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Emotional Factor Questions</Label>
            

            
            <OptionsContainer>
              {Object.entries(formData.journalConfig?.emotionalQuestions || {}).map(([key, question]) => (
                <OptionGroup key={key}>
                  <OptionInput
                    type="text"
                    value={question}
                    onChange={(e) => {
                      const newEmotionalQuestions = {
                        ...formData.journalConfig?.emotionalQuestions,
                        [key]: e.target.value
                      };
                      handleJournalConfigChange('emotionalQuestions', newEmotionalQuestions);
                    }}
                    placeholder={`Question for ${key}`}
                  />
                  <span style={{ 
                    fontSize: theme.typography.sizes.small, 
                    color: theme.colors.textLight,
                    minWidth: '120px',
                    textAlign: 'right'
                  }}>
                    {key}
                  </span>
                </OptionGroup>
              ))}
            </OptionsContainer>
          </FormGroup>

          <FormGroup>
            <Label>Emotional Factor Labels</Label>
            <OptionsContainer>
              {Object.entries(formData.journalConfig?.emotionalLabels || {}).map(([key, labels]) => (
                <div key={key} style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: theme.spacing.sm,
                  padding: theme.spacing.sm,
                  border: `1px solid ${theme.colors.doryLighterBlue}`,
                  borderRadius: theme.borderRadius.md
                }}>
                  <div style={{ 
                    fontSize: theme.typography.sizes.small, 
                    color: theme.colors.textLight,
                    fontWeight: theme.typography.weights.medium,
                    marginBottom: theme.spacing.xs
                  }}>
                    {key}
                  </div>
                  <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                    <OptionInput
                      type="text"
                      value={labels.low || ''}
                      onChange={(e) => {
                        const newEmotionalLabels = {
                          ...formData.journalConfig?.emotionalLabels,
                          [key]: {
                            ...labels,
                            low: e.target.value
                          }
                        };
                        handleJournalConfigChange('emotionalLabels', newEmotionalLabels);
                      }}
                      placeholder="Low label"
                    />
                    <OptionInput
                      type="text"
                      value={labels.high || ''}
                      onChange={(e) => {
                        const newEmotionalLabels = {
                          ...formData.journalConfig?.emotionalLabels,
                          [key]: {
                            ...labels,
                            high: e.target.value
                          }
                        };
                        handleJournalConfigChange('emotionalLabels', newEmotionalLabels);
                      }}
                      placeholder="High label"
                    />
                  </div>
                </div>
              ))}
            </OptionsContainer>
          </FormGroup>
        </>
      )}

      {type === 'checkpoint' && ['question', 'quiz'].includes(formData.type) && (
        <>
          <FormGroup>
            <Label>Question *</Label>
            

            
            <TextArea
              value={formData.content.question}
              onChange={(e) => handleInputChange('question', e.target.value, 'content')}
              placeholder="Enter your question"
            />
          </FormGroup>

          <FormGroup>
            <Label>Answer Options *</Label>
            <OptionsContainer>
              {formData.content.options.map((option, index) => (
                <OptionGroup key={index}>
                  <OptionInput
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                  />
                  {formData.content.options.length > 1 && (
                    <RemoveButton onClick={() => removeOption(index)}>
                      Remove
                    </RemoveButton>
                  )}
                </OptionGroup>
              ))}
              <AddOptionButton onClick={addOption}>
                + Add Option
              </AddOptionButton>
            </OptionsContainer>
          </FormGroup>

          {formData.type === 'quiz' && (
            <>
              <FormGroup>
                <Label>Correct Answer</Label>
                <Select
                  value={formData.content.correctAnswer}
                  onChange={(e) => handleInputChange('correctAnswer', parseInt(e.target.value), 'content')}
                >
                  {formData.content.options.map((_, index) => (
                    <option key={index} value={index}>
                      Option {index + 1}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Confirmation Statement (Optional)</Label>
                

                
                <TextArea
                  value={formData.content.confirmationStatement || ''}
                  onChange={(e) => handleInputChange('confirmationStatement', e.target.value, 'content')}
                  placeholder="Enter a message to display when the correct answer is selected (e.g., 'Great job! That\'s exactly right.')"
                  rows={3}
                />
                <small style={{ color: theme.colors.textLight, fontSize: '0.875rem' }}>
                  This message will appear below the correct answer after the user selects it.
                </small>
              </FormGroup>
            </>
          )}
        </>
      )}

      {type === 'checkpoint' && formData.type === 'yes_no' && (
        <FormGroup>
          <Label>Yes/No Question *</Label>
          

          
          <TextArea
            value={formData.content.question}
            onChange={(e) => handleInputChange('question', e.target.value, 'content')}
            placeholder="Enter your yes/no question"
          />
        </FormGroup>
      )}

      {type === 'checkpoint' && formData.type === 'link' && (
        <FormGroup>
          <Label>Link URL</Label>
          <Input
            type="url"
            value={formData.content.linkUrl}
            onChange={(e) => handleInputChange('linkUrl', e.target.value, 'content')}
            placeholder="https://example.com"
          />
        </FormGroup>
      )}

      {/* Email Capture Configuration */}
      {type === 'checkpoint' && formData.type === 'email_capture' && (
        <>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.content?.showFullName || false}
                onChange={(e) => handleInputChange('showFullName', e.target.checked, 'content')}
                style={{ marginRight: '8px' }}
              />
              Show Full Name Field
            </Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              When checked, users will see a full name input field above the email field
            </div>
          </FormGroup>
          
          {formData.content?.showFullName && (
            <FormGroup>
              <Label>Full Name Field Placeholder Text</Label>
              <Input
                type="text"
                value={formData.content.fullNamePlaceholder || 'Enter your full name'}
                onChange={(e) => handleInputChange('fullNamePlaceholder', e.target.value, 'content')}
                placeholder="Enter your full name"
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px', 
                fontStyle: 'italic' 
              }}>
                This text appears as placeholder in the full name input field (only visible when "Show Full Name Field" is checked)
              </div>
            </FormGroup>
          )}
          
          <FormGroup>
            <Label>Email Field Placeholder Text</Label>
            <Input
              type="text"
              value={formData.content.emailPlaceholder || 'Enter your email address'}
              onChange={(e) => handleInputChange('emailPlaceholder', e.target.value, 'content')}
              placeholder="Enter your email address"
            />
          </FormGroup>
          
          
          <FormGroup>
            <Label>Image URL (Optional)</Label>
            <Input
              type="url"
              value={formData.content.imageUrl || ''}
              onChange={(e) => handleInputChange('imageUrl', e.target.value, 'content')}
              placeholder="https://example.com/image.jpg"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter an image URL to display an image at the top of the email capture screen. Supports JPG, PNG, GIF, and other image formats.
            </div>
          </FormGroup>
          
          {formData.content?.imageUrl && (
            <FormGroup>
              <Label>Image Alt Text (Optional)</Label>
              <Input
                type="text"
                value={formData.content.imageAlt || ''}
                onChange={(e) => handleInputChange('imageAlt', e.target.value, 'content')}
                placeholder="Descriptive text for screen readers"
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px', 
                fontStyle: 'italic' 
              }}>
                Provide alternative text for accessibility. This text will be read by screen readers.
              </div>
            </FormGroup>
          )}
          
          <FormGroup>
            <Label>Video URL (Optional)</Label>
            <Input
              type="url"
              value={formData.content.videoUrl || ''}
              onChange={(e) => handleInputChange('videoUrl', e.target.value, 'content')}
              placeholder="https://example.com/video.mp4"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter a video URL to display an auto-playing video at the top of the email capture screen. Supports MP4, WebM, and other video formats.
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>ActiveCampaign Tag ID (Optional)</Label>
            <Input
              type="text"
              value={formData.content.activeCampaignTagId || ''}
              onChange={(e) => handleInputChange('activeCampaignTagId', e.target.value, 'content')}
              placeholder="e.g., 123"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the ActiveCampaign Tag ID to automatically apply when this email is captured. Leave empty for no tag.
            </div>
          </FormGroup>
        </>
      )}

      {/* Phone Verification Configuration */}
      {type === 'checkpoint' && formData.type === 'phone_verification' && (
        <>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.content?.showFullName || false}
                onChange={(e) => handleInputChange('showFullName', e.target.checked, 'content')}
                style={{ marginRight: '8px' }}
              />
              Show Full Name Field
            </Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              When checked, users will see a full name input field above the phone field
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.content?.showEmail || false}
                onChange={(e) => handleInputChange('showEmail', e.target.checked, 'content')}
                style={{ marginRight: '8px' }}
              />
              Show Email Field
            </Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              When checked, users will see an email input field above the phone field
            </div>
          </FormGroup>
          
          {formData.content?.showFullName && (
            <FormGroup>
              <Label>Full Name Field Placeholder Text</Label>
              <Input
                type="text"
                value={formData.content.fullNamePlaceholder || 'Enter your full name'}
                onChange={(e) => handleInputChange('fullNamePlaceholder', e.target.value, 'content')}
                placeholder="Enter your full name"
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px', 
                fontStyle: 'italic' 
              }}>
                This text appears as placeholder in the full name input field (only visible when "Show Full Name Field" is checked)
              </div>
            </FormGroup>
          )}
          
          {formData.content?.showEmail && (
            <FormGroup>
              <Label>Email Field Placeholder Text</Label>
              <Input
                type="text"
                value={formData.content.emailPlaceholder || 'Enter your email address (optional)'}
                onChange={(e) => handleInputChange('emailPlaceholder', e.target.value, 'content')}
                placeholder="Enter your email address (optional)"
              />
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px', 
                fontStyle: 'italic' 
              }}>
                This text appears as placeholder in the email input field (only visible when "Show Email Field" is checked)
              </div>
            </FormGroup>
          )}
          
          <FormGroup>
            <Label>Phone Field Placeholder Text</Label>
            <Input
              type="text"
              value={formData.content.phonePlaceholder || 'Enter your phone number (e.g., 555-123-4567)'}
              onChange={(e) => handleInputChange('phonePlaceholder', e.target.value, 'content')}
              placeholder="Enter your phone number (e.g., 555-123-4567)"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Default Country Code</Label>
            <Select
              value={formData.content.defaultCountryCode || '+1'}
              onChange={(e) => handleInputChange('defaultCountryCode', e.target.value, 'content')}
            >
              <option value="+1">🇺🇸 +1 (United States)</option>
              <option value="+1">🇨🇦 +1 (Canada)</option>
              <option value="+44">🇬🇧 +44 (United Kingdom)</option>
              <option value="+61">🇦🇺 +61 (Australia)</option>
              <option value="+49">🇩🇪 +49 (Germany)</option>
              <option value="+33">🇫🇷 +33 (France)</option>
              <option value="+39">🇮🇹 +39 (Italy)</option>
              <option value="+34">🇪🇸 +34 (Spain)</option>
              <option value="+31">🇳🇱 +31 (Netherlands)</option>
              <option value="+46">🇸🇪 +46 (Sweden)</option>
              <option value="+47">🇳🇴 +47 (Norway)</option>
              <option value="+45">🇩🇰 +45 (Denmark)</option>
              <option value="+358">🇫🇮 +358 (Finland)</option>
              <option value="+41">🇨🇭 +41 (Switzerland)</option>
              <option value="+43">🇦🇹 +43 (Austria)</option>
              <option value="+32">🇧🇪 +32 (Belgium)</option>
              <option value="+351">🇵🇹 +351 (Portugal)</option>
              <option value="+353">🇮🇪 +353 (Ireland)</option>
              <option value="+48">🇵🇱 +48 (Poland)</option>
              <option value="+420">🇨🇿 +420 (Czech Republic)</option>
              <option value="+36">🇭🇺 +36 (Hungary)</option>
              <option value="+30">🇬🇷 +30 (Greece)</option>
              <option value="+90">🇹🇷 +90 (Turkey)</option>
              <option value="+7">🇷🇺 +7 (Russia)</option>
              <option value="+81">🇯🇵 +81 (Japan)</option>
              <option value="+82">🇰🇷 +82 (South Korea)</option>
              <option value="+86">🇨🇳 +86 (China)</option>
              <option value="+91">🇮🇳 +91 (India)</option>
              <option value="+55">🇧🇷 +55 (Brazil)</option>
              <option value="+52">🇲🇽 +52 (Mexico)</option>
              <option value="+54">🇦🇷 +54 (Argentina)</option>
              <option value="+56">🇨🇱 +56 (Chile)</option>
              <option value="+57">🇨🇴 +57 (Colombia)</option>
              <option value="+58">🇻🇪 +58 (Venezuela)</option>
              <option value="+51">🇵🇪 +51 (Peru)</option>
              <option value="+593">🇪🇨 +593 (Ecuador)</option>
              <option value="+595">🇵🇾 +595 (Paraguay)</option>
              <option value="+598">🇺🇾 +598 (Uruguay)</option>
              <option value="+27">🇿🇦 +27 (South Africa)</option>
              <option value="+234">🇳🇬 +234 (Nigeria)</option>
            </Select>
          </FormGroup>
          
          
          <FormGroup>
            <Label>Video URL (Optional)</Label>
            <Input
              type="url"
              value={formData.content.videoUrl || ''}
              onChange={(e) => handleInputChange('videoUrl', e.target.value, 'content')}
              placeholder="https://example.com/video.mp4"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter a video URL to display an auto-playing video at the top of the phone verification screen. Supports MP4, WebM, and other video formats.
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>ActiveCampaign Tag ID (Optional)</Label>
            <Input
              type="text"
              value={formData.content.activeCampaignTagId || ''}
              onChange={(e) => handleInputChange('activeCampaignTagId', e.target.value, 'content')}
              placeholder="e.g., 123"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the ActiveCampaign Tag ID to automatically apply when phone verification is completed. Leave empty for no tag.
            </div>
          </FormGroup>
        </>
      )}

      {/* Payment Configuration */}
      {type === 'checkpoint' && formData.type === 'payment' && (
        <>
          <FormGroup>
            <Label>Dollar Amount (USD) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.content.dollarAmount || 1.00}
              onChange={(e) => {
                const amount = parseFloat(e.target.value) || 0;
                // Store dollar amount directly in stripeAmount
                handleInputChange('stripeAmount', amount, 'content');
                handleInputChange('dollarAmount', amount, 'content');
              }}
              placeholder="1.00"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the amount in dollars (e.g., 29.99 for $29.99). This will be charged to visitors.
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Product Name (Optional)</Label>
            <Input
              type="text"
              value={formData.content.productName || ''}
              onChange={(e) => handleInputChange('productName', e.target.value, 'content')}
              placeholder="Premium Course"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Display name for the product being purchased (optional)
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Product Image URL (Optional)</Label>
            <Input
              type="url"
              value={formData.content.productImage || ''}
              onChange={(e) => handleInputChange('productImage', e.target.value, 'content')}
              placeholder="https://example.com/product-image.jpg"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              URL to the product image. Should be a square or rectangular image for best results.
              You can upload to any image hosting service and paste the direct link here.
            </div>
            
            {/* Image preview */}
            {formData.content?.productImage && (
              <div style={{ 
                marginTop: theme.spacing.sm,
                textAlign: 'center'
              }}>
                <img 
                  src={formData.content.productImage} 
                  alt="Product preview"
                  style={{
                    maxWidth: '312px',
                    maxHeight: '312px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </FormGroup>
          
          
          <FormGroup>
            <Label>ActiveCampaign Tag ID (Optional)</Label>
            <Input
              type="text"
              value={formData.content.activeCampaignTagId || ''}
              onChange={(e) => handleInputChange('activeCampaignTagId', e.target.value, 'content')}
              placeholder="e.g., 123"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the ActiveCampaign Tag ID to automatically apply when payment is completed. Leave empty for no tag.
            </div>
          </FormGroup>
        </>
      )}

      {/* Payment Simple Configuration */}
      {type === 'checkpoint' && formData.type === 'payment_simple' && (
        <>
          <FormGroup>
            <Label>Dollar Amount (USD) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.content.dollarAmount || 1.00}
              onChange={(e) => {
                const amount = parseFloat(e.target.value) || 0;
                // Store dollar amount directly in stripeAmount
                handleInputChange('stripeAmount', amount, 'content');
                handleInputChange('dollarAmount', amount, 'content');
              }}
              placeholder="1.00"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the amount in dollars (e.g., 29.99 for $29.99). This will be charged to visitors.
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Product Name (Optional)</Label>
            <Input
              type="text"
              value={formData.content.productName || ''}
              onChange={(e) => handleInputChange('productName', e.target.value, 'content')}
              placeholder="Premium Course"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Display name for the product being purchased (optional)
            </div>
          </FormGroup>
          
          
          <FormGroup>
            <Label>ActiveCampaign Tag ID (Optional)</Label>
            <Input
              type="text"
              value={formData.content.activeCampaignTagId || ''}
              onChange={(e) => handleInputChange('activeCampaignTagId', e.target.value, 'content')}
              placeholder="e.g., 123"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the ActiveCampaign Tag ID to automatically apply when payment is completed. Leave empty for no tag.
            </div>
          </FormGroup>
        </>
      )}

      {/* Payment Membership Configuration */}
      {type === 'checkpoint' && formData.type === 'payment_membership' && (
        <>
          <FormGroup>
            <Label>CTA Button Text</Label>
            <Input
              type="text"
              value={formData.content?.ctaText || ''}
              onChange={(e) => handleInputChange('ctaText', e.target.value, 'content')}
              placeholder="Subscribe Now"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Text for the subscription button
            </div>
          </FormGroup>

          <FormGroup>
            <Label>ActiveCampaign Tag ID (Optional)</Label>
            <Input
              type="text"
              value={formData.content.activeCampaignTagId || ''}
              onChange={(e) => handleInputChange('activeCampaignTagId', e.target.value, 'content')}
              placeholder="e.g., 123"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the ActiveCampaign Tag ID to automatically apply when subscription is completed. Leave empty for no tag.
            </div>
          </FormGroup>
        </>
      )}

      {/* Marketing Landing Page Configuration */}
      {type === 'checkpoint' && formData.type === 'marketing_landing_page' && (
        <>
          <FormGroup>
            <Label>Hero Image URL *</Label>
            <Input
              type="url"
              value={formData.content.imageUrl || ''}
              onChange={(e) => handleInputChange('imageUrl', e.target.value, 'content')}
              placeholder="https://example.com/hero-image.jpg"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Large promotional image displayed at the top of the landing page
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Hero Image Alt Text (Optional)</Label>
            <Input
              type="text"
              value={formData.content.imageAlt || ''}
              onChange={(e) => handleInputChange('imageAlt', e.target.value, 'content')}
              placeholder="Promotional image for the product"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Descriptive text for accessibility (recommended)
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Dollar Amount (USD) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.content.dollarAmount || 1.00}
              onChange={(e) => {
                const amount = parseFloat(e.target.value) || 0;
                // Store dollar amount directly in stripeAmount
                handleInputChange('stripeAmount', amount, 'content');
                handleInputChange('dollarAmount', amount, 'content');
              }}
              placeholder="1.00"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the amount in dollars (e.g., 29.99 for $29.99). This will be charged to visitors.
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Product Name (Optional)</Label>
            <Input
              type="text"
              value={formData.content.productName || ''}
              onChange={(e) => handleInputChange('productName', e.target.value, 'content')}
              placeholder="Premium Course"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Display name for the product being purchased (optional)
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Product Image URL (Optional)</Label>
            <Input
              type="url"
              value={formData.content.productImage || ''}
              onChange={(e) => handleInputChange('productImage', e.target.value, 'content')}
              placeholder="https://example.com/product-image.jpg"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Product image displayed in the payment form (optional)
            </div>
          </FormGroup>
          
          
          <FormGroup>
            <Label>ActiveCampaign Tag ID (Optional)</Label>
            <Input
              type="text"
              value={formData.content.activeCampaignTagId || ''}
              onChange={(e) => handleInputChange('activeCampaignTagId', e.target.value, 'content')}
              placeholder="123"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Enter the ActiveCampaign Tag ID to automatically apply when payment is completed. Leave empty for no tag.
            </div>
          </FormGroup>
        </>
      )}

      {/* Testimonial Configuration */}
      {formData.type === 'testimonial' && (
        <>
          <FormGroup>
            <Label>Author Name</Label>
            <Input
              type="text"
              value={formData.content?.authorName || ''}
              onChange={(e) => handleInputChange('authorName', e.target.value, 'content')}
              placeholder="John Smith"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The name of the person giving the testimonial
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Author Title/Position (Optional)</Label>
            <Input
              type="text"
              value={formData.content?.authorTitle || ''}
              onChange={(e) => handleInputChange('authorTitle', e.target.value, 'content')}
              placeholder="CEO, Acme Corp"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Optional title or position (e.g., "CEO, Acme Corp" or "Marketing Director")
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Author Image URL</Label>
            <Input
              type="url"
              value={formData.content?.authorImage || ''}
              onChange={(e) => handleInputChange('authorImage', e.target.value, 'content')}
              placeholder="https://example.com/author-photo.jpg"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              URL to the author's photo. Should be a square image for best results. 
              You can upload to any image hosting service (Imgur, Google Drive, etc.) and paste the direct link here.
            </div>
            
            {/* Image preview */}
            {formData.content?.authorImage && (
              <div style={{ 
                marginTop: theme.spacing.sm,
                textAlign: 'center'
              }}>
                <img 
                  src={formData.content.authorImage} 
                  alt="Author preview"
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `2px solid ${theme.colors.doryLighterBlue}`,
                    boxShadow: theme.shadows.sm
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: theme.colors.textLight,
                  marginTop: theme.spacing.xs
                }}>
                  Preview
                </div>
              </div>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label>Start Pace (Optional)</Label>
            <Input
              type="number"
              min="0"
              max="18"
              value={formData.content?.startPace || ''}
              onChange={(e) => handleInputChange('startPace', e.target.value, 'content')}
              placeholder="0"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The starting pace value (0-18) for the pace visualization bar
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>End Pace (Optional)</Label>
            <Input
              type="number"
              min="0"
              max="18"
              value={formData.content?.endPace || ''}
              onChange={(e) => handleInputChange('endPace', e.target.value, 'content')}
              placeholder="18"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The ending pace value (0-18) for the pace visualization bar. If both start and end pace are provided, a pace visualization will be displayed.
            </div>
          </FormGroup>
        </>
      )}

      {/* As Seen In Configuration */}
      {formData.type === 'as_seen_in' && (
        <>
          <FormGroup>
            <Label>Brand Image URL</Label>
            <Input
              type="url"
              value={formData.content?.brandImage || ''}
              onChange={(e) => handleInputChange('brandImage', e.target.value, 'content')}
              placeholder="https://example.com/brand-logo.png"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              URL to the brand logo image. Should be a horizontal logo for best results.
              You can upload to any image hosting service and paste the direct link here.
            </div>
            
            {/* Image preview */}
            {formData.content?.brandImage && (
              <div style={{ 
                marginTop: theme.spacing.sm,
                textAlign: 'center'
              }}>
                <img 
                  src={formData.content.brandImage} 
                  alt="Brand logo preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '80px',
                    objectFit: 'contain',
                    border: `2px solid ${theme.colors.doryLighterBlue}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.sm
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: theme.colors.textLight,
                  marginTop: theme.spacing.xs
                }}>
                  Brand Logo Preview
                </div>
              </div>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label>Brand Name (Optional)</Label>
            <Input
              type="text"
              value={formData.content?.brandName || ''}
              onChange={(e) => handleInputChange('brandName', e.target.value, 'content')}
              placeholder="Forbes, Inc., TechCrunch"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The name of the brand for accessibility purposes
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Article Category</Label>
            <Input
              type="text"
              value={formData.content?.category || ''}
              onChange={(e) => handleInputChange('category', e.target.value, 'content')}
              placeholder="PRODUCTIVITY"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Category label that appears above the headline (e.g., "PRODUCTIVITY", "ENTREPRENEURSHIP")
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Article Headline</Label>
            <TextArea
              value={formData.content?.headline || ''}
              onChange={(e) => handleInputChange('headline', e.target.value, 'content')}
              placeholder="How to Avoid Burnout by Finding Your Optimal Pace as an Entrepreneur"
              rows={3}
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Main headline text that appears in large, bold font
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Article Subheading (Optional)</Label>
            <TextArea
              value={formData.content?.subheading || ''}
              onChange={(e) => handleInputChange('subheading', e.target.value, 'content')}
              placeholder="You can't sprint a marathon. Discover your ideal speed to increase your chances of winning."
              rows={3}
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Subheading text that appears below the headline in lighter gray font
            </div>
          </FormGroup>
          
          <FormGroup>
            <Label>Article URL</Label>
            <Input
              type="url"
              value={formData.content?.articleUrl || ''}
              onChange={(e) => handleInputChange('articleUrl', e.target.value, 'content')}
              placeholder="https://example.com/article-link"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              URL to the article. The "Read Article" button will link to this URL.
            </div>
          </FormGroup>
        </>
      )}

      {/* Media Appearance Configuration */}
      {formData.type === 'media_appearance' && (
        <>
          <FormGroup>
            <Label>Logo Image URL</Label>
            <Input
              type="url"
              value={formData.content?.logoImage || ''}
              onChange={(e) => handleInputChange('logoImage', e.target.value, 'content')}
              placeholder="https://example.com/media-logo.png"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              URL to the media appearance logo image. Should be a horizontal logo for best results.
              You can upload to any image hosting service and paste the direct link here.
            </div>
            
            {/* Image preview */}
            {formData.content?.logoImage && (
              <div style={{ 
                marginTop: theme.spacing.sm,
                textAlign: 'center'
              }}>
                <img 
                  src={formData.content.logoImage} 
                  alt="Logo preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '80px',
                    objectFit: 'contain',
                    border: `2px solid ${theme.colors.doryLighterBlue}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.sm
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: theme.colors.textLight,
                  marginTop: theme.spacing.xs
                }}>
                  Logo Preview
                </div>
              </div>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label>Logo Alt Text (Optional)</Label>
            <Input
              type="text"
              value={formData.content?.logoAlt || ''}
              onChange={(e) => handleInputChange('logoAlt', e.target.value, 'content')}
              placeholder="Media Appearance Logo"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Alt text for accessibility purposes
            </div>
          </FormGroup>
        </>
      )}

      {/* Membership Details Configuration */}
      {formData.type === 'membership_details' && (
        <>
          <FormGroup>
            <Label>Value Boxes</Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '8px', 
              fontStyle: 'italic' 
            }}>
              Configure the three value boxes that explain membership benefits. Each box needs a title, description, and icon.
            </div>
            
            {formData.content?.valueBoxes?.map((box, index) => (
              <div key={index} style={{
                border: `1px solid ${theme.colors.doryLighterBlue}`,
                borderRadius: theme.borderRadius.md,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.sm,
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: theme.spacing.sm
                }}>
                  <span style={{ 
                    fontWeight: theme.typography.weights.semiBold,
                    color: theme.colors.doryBlue
                  }}>
                    Value Box {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newValueBoxes = [...(formData.content?.valueBoxes || [])];
                      newValueBoxes.splice(index, 1);
                      handleInputChange('valueBoxes', newValueBoxes, 'content');
                    }}
                    style={{
                      background: theme.colors.error,
                      color: 'white',
                      border: 'none',
                      borderRadius: theme.borderRadius.sm,
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '12px', 
                      fontWeight: theme.typography.weights.medium,
                      marginBottom: '4px',
                      color: theme.colors.text
                    }}>
                      Title *
                    </label>
                    <Input
                      type="text"
                      value={box.title || ''}
                      onChange={(e) => {
                        const newValueBoxes = [...(formData.content?.valueBoxes || [])];
                        newValueBoxes[index] = { ...box, title: e.target.value };
                        handleInputChange('valueBoxes', newValueBoxes, 'content');
                      }}
                      placeholder="Tools"
                      style={{ fontSize: '14px' }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '12px', 
                      fontWeight: theme.typography.weights.medium,
                      marginBottom: '4px',
                      color: theme.colors.text
                    }}>
                      Icon (Emoji)
                    </label>
                    <Input
                      type="text"
                      value={box.icon || ''}
                      onChange={(e) => {
                        const newValueBoxes = [...(formData.content?.valueBoxes || [])];
                        newValueBoxes[index] = { ...box, icon: e.target.value };
                        handleInputChange('valueBoxes', newValueBoxes, 'content');
                      }}
                      placeholder="🛠️"
                      style={{ fontSize: '14px' }}
                    />
                  </div>
                </div>
                
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: theme.typography.weights.medium,
                    marginBottom: '4px',
                    color: theme.colors.text
                  }}>
                    Image URL (Optional)
                  </label>
                  <Input
                    type="url"
                    value={box.image || ''}
                    onChange={(e) => {
                      const newValueBoxes = [...(formData.content?.valueBoxes || [])];
                      newValueBoxes[index] = { ...box, image: e.target.value };
                      handleInputChange('valueBoxes', newValueBoxes, 'content');
                    }}
                    placeholder="https://example.com/icon.png"
                    style={{ fontSize: '14px' }}
                  />
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#666', 
                    marginTop: '4px', 
                    fontStyle: 'italic' 
                  }}>
                    URL to an image icon. If provided, this will be used instead of the emoji icon. Should be a square image for best results.
                  </div>
                  
                  {/* Image preview */}
                  {box.image && (
                    <div style={{ 
                      marginTop: theme.spacing.sm,
                      textAlign: 'center'
                    }}>
                      <img 
                        src={box.image} 
                        alt="Icon preview"
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: `2px solid ${theme.colors.doryLighterBlue}`,
                          boxShadow: theme.shadows.sm
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div style={{ 
                        fontSize: '10px', 
                        color: theme.colors.textLight,
                        marginTop: theme.spacing.xs
                      }}>
                        Preview
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: theme.typography.weights.medium,
                    marginBottom: '4px',
                    color: theme.colors.text
                  }}>
                    Description *
                  </label>
                  <TextArea
                    value={box.description || ''}
                    onChange={(e) => {
                      const newValueBoxes = [...(formData.content?.valueBoxes || [])];
                      newValueBoxes[index] = { ...box, description: e.target.value };
                      handleInputChange('valueBoxes', newValueBoxes, 'content');
                    }}
                    placeholder="Easily and precisely track and improve your Optimal Pace"
                    rows={2}
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>
            )) || []}
            
            <button
              type="button"
              onClick={() => {
                const newValueBoxes = [...(formData.content?.valueBoxes || []), { title: '', description: '', icon: '', image: '' }];
                handleInputChange('valueBoxes', newValueBoxes, 'content');
              }}
              style={{
                background: theme.colors.doryBlue,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.xs,
                marginTop: theme.spacing.sm
              }}
            >
              + Add Value Box
            </button>
          </FormGroup>
        </>
      )}

      {/* Study Details Configuration */}
      {formData.type === 'study_details' && (
        <>
          <FormGroup>
            <Label>Study Box</Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '8px', 
              fontStyle: 'italic' 
            }}>
              Configure the study box that highlights your clinical trial backed research. This will appear as a single dark blue feature box.
            </div>
            
            <div style={{
              border: `1px solid ${theme.colors.doryLighterBlue}`,
              borderRadius: theme.borderRadius.md,
              padding: theme.spacing.md,
              marginBottom: theme.spacing.sm,
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: theme.spacing.sm
              }}>
                <span style={{ 
                  fontWeight: theme.typography.weights.semiBold,
                  color: theme.colors.doryBlue
                }}>
                  Study Research Box
                </span>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: theme.typography.weights.medium,
                    marginBottom: '4px',
                    color: theme.colors.text
                  }}>
                    Title *
                  </label>
                  <Input
                    type="text"
                    value={formData.content?.studyBox?.title || ''}
                    onChange={(e) => {
                      handleInputChange('studyBox', { 
                        ...formData.content?.studyBox, 
                        title: e.target.value 
                      }, 'content');
                    }}
                    placeholder="Clinical Trial Backed Research"
                    style={{ fontSize: '14px' }}
                  />
                </div>
                
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: theme.typography.weights.medium,
                    marginBottom: '4px',
                    color: theme.colors.text
                  }}>
                    Icon (Emoji)
                  </label>
                  <Input
                    type="text"
                    value={formData.content?.studyBox?.icon || ''}
                    onChange={(e) => {
                      handleInputChange('studyBox', { 
                        ...formData.content?.studyBox, 
                        icon: e.target.value 
                      }, 'content');
                    }}
                    placeholder="🔬"
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  fontWeight: theme.typography.weights.medium,
                  marginBottom: '4px',
                  color: theme.colors.text
                }}>
                  Image URL (Optional)
                </label>
                <Input
                  type="url"
                  value={formData.content?.studyBox?.image || ''}
                  onChange={(e) => {
                    handleInputChange('studyBox', { 
                      ...formData.content?.studyBox, 
                      image: e.target.value 
                    }, 'content');
                  }}
                  placeholder="https://example.com/study-icon.png"
                  style={{ fontSize: '14px' }}
                />
                <div style={{ 
                  fontSize: '10px', 
                  color: '#666', 
                  marginTop: '4px', 
                  fontStyle: 'italic' 
                }}>
                  URL to an image icon. If provided, this will be used instead of the emoji icon. Should be a square image for best results.
                </div>
                
                {/* Image preview */}
                {formData.content?.studyBox?.image && (
                  <div style={{ 
                    marginTop: theme.spacing.sm,
                    textAlign: 'center'
                  }}>
                    <img 
                      src={formData.content?.studyBox?.image} 
                      alt="Icon preview"
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: `2px solid ${theme.colors.doryLighterBlue}`,
                        boxShadow: theme.shadows.sm
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div style={{ 
                      fontSize: '10px', 
                      color: theme.colors.textLight,
                      marginTop: theme.spacing.xs
                    }}>
                      Preview
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  fontWeight: theme.typography.weights.medium,
                  marginBottom: '4px',
                  color: theme.colors.text
                }}>
                  Description *
                </label>
                <TextArea
                  value={formData.content?.studyBox?.description || ''}
                  onChange={(e) => {
                    handleInputChange('studyBox', { 
                      ...formData.content?.studyBox, 
                      description: e.target.value 
                    }, 'content');
                  }}
                  placeholder="Our tools and insights are powered by rigorous clinical trial research, ensuring evidence-based approaches to mental performance optimization."
                  rows={3}
                  style={{ fontSize: '14px' }}
                />
              </div>
            </div>
          </FormGroup>
        </>
      )}

      {/* Community Members Configuration */}
      {formData.type === 'community_members' && (
        <>
          <FormGroup>
            <Label>Community Members</Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '8px', 
              fontStyle: 'italic' 
            }}>
              Add community members to display in the grid. Each member needs a name and optionally a title and image URL.
            </div>
            
            {formData.content?.members?.map((member, index) => (
              <div key={index} style={{
                border: `1px solid ${theme.colors.doryLighterBlue}`,
                borderRadius: theme.borderRadius.md,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.sm,
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: theme.spacing.sm
                }}>
                  <span style={{ 
                    fontWeight: theme.typography.weights.semiBold,
                    color: theme.colors.doryBlue
                  }}>
                    Member {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newMembers = [...(formData.content?.members || [])];
                      newMembers.splice(index, 1);
                      handleInputChange('members', newMembers, 'content');
                    }}
                    style={{
                      background: theme.colors.error,
                      color: 'white',
                      border: 'none',
                      borderRadius: theme.borderRadius.sm,
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '12px', 
                      fontWeight: theme.typography.weights.medium,
                      marginBottom: '4px',
                      color: theme.colors.text
                    }}>
                      Name *
                    </label>
                    <Input
                      type="text"
                      value={member.name || ''}
                      onChange={(e) => {
                        const newMembers = [...(formData.content?.members || [])];
                        newMembers[index] = { ...member, name: e.target.value };
                        handleInputChange('members', newMembers, 'content');
                      }}
                      placeholder="John Smith"
                      style={{ fontSize: '14px' }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '12px', 
                      fontWeight: theme.typography.weights.medium,
                      marginBottom: '4px',
                      color: theme.colors.text
                    }}>
                      Title (Optional)
                    </label>
                    <Input
                      type="text"
                      value={member.title || ''}
                      onChange={(e) => {
                        const newMembers = [...(formData.content?.members || [])];
                        newMembers[index] = { ...member, title: e.target.value };
                        handleInputChange('members', newMembers, 'content');
                      }}
                      placeholder="CEO, Acme Corp"
                      style={{ fontSize: '14px' }}
                    />
                  </div>
                </div>
                
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    fontWeight: theme.typography.weights.medium,
                    marginBottom: '4px',
                    color: theme.colors.text
                  }}>
                    Image URL (Optional)
                  </label>
                  <Input
                    type="url"
                    value={member.image || ''}
                    onChange={(e) => {
                      const newMembers = [...(formData.content?.members || [])];
                      newMembers[index] = { ...member, image: e.target.value };
                      handleInputChange('members', newMembers, 'content');
                    }}
                    placeholder="https://example.com/photo.jpg"
                    style={{ fontSize: '14px' }}
                  />
                  
                  {/* Image preview */}
                  {member.image && (
                    <div style={{ 
                      marginTop: theme.spacing.sm,
                      textAlign: 'center'
                    }}>
                      <img 
                        src={member.image} 
                        alt="Member preview"
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: `2px solid ${theme.colors.doryLighterBlue}`,
                          boxShadow: theme.shadows.sm
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div style={{ 
                        fontSize: '10px', 
                        color: theme.colors.textLight,
                        marginTop: theme.spacing.xs
                      }}>
                        Preview
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )) || []}
            
            <button
              type="button"
              onClick={() => {
                const newMembers = [...(formData.content?.members || []), { name: '', title: '', image: '' }];
                handleInputChange('members', newMembers, 'content');
              }}
              style={{
                background: theme.colors.doryBlue,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.xs,
                marginTop: theme.spacing.sm
              }}
            >
              + Add Member
            </button>
          </FormGroup>
        </>
      )}

      {/* Leaderboard Configuration */}
      {formData.type === 'leaderboard' && (
        <>
          <FormGroup>
            <Label>Leaderboard Configuration</Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '8px', 
              fontStyle: 'italic' 
            }}>
              The leaderboard automatically displays top performers based on Optimal Pace × Mental Resilience scores.
              Users must have both metrics calculated, at least 3 journal entries, and non-zero values to appear.
            </div>
            
            <div style={{
              backgroundColor: '#f8f9fa',
              border: `1px solid ${theme.colors.doryLighterBlue}`,
              borderRadius: theme.borderRadius.md,
              padding: theme.spacing.md,
              fontSize: '14px',
              color: theme.colors.textLight
            }}>
              <strong>Automatic Features:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Fetches all users from the database</li>
                <li>Filters users with valid Optimal Pace and Mental Resilience metrics</li>
                <li>Requires minimum 3 journal entries</li>
                <li>Calculates aggregate score (Optimal Pace × Mental Resilience)</li>
                <li>Shows user's first name if available, otherwise shortcode</li>
                <li>Displays most recent journal entry date</li>
                <li>Mobile-responsive design with special styling for top 3</li>
              </ul>
            </div>
          </FormGroup>
        </>
      )}

      {/* Membership Entry Configuration */}
      {formData.type === 'membership_entry' && (
        <>
          <FormGroup>
            <Label>Membership Entry Configuration</Label>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '8px', 
              fontStyle: 'italic' 
            }}>
              This screen collects user information including name, email, phone, location, and unique abilities. All fields are required and data is automatically saved to userInfo.
            </div>
          </FormGroup>
        </>
      )}

      {/* Open Graph Meta Tags for Learning and Media Appearance Screens */}
      {(formData.type === 'learning' || formData.type === 'media_appearance') && (
        <FormGroup>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing.sm,
            padding: theme.spacing.md,
            backgroundColor: '#f8f9fa',
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.doryLighterBlue}`,
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onClick={() => setIsOgSectionExpanded(!isOgSectionExpanded)}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
          >
            <div>
              <Label style={{ margin: 0, cursor: 'pointer' }}>
                Open Graph Meta Tags (Optional)
              </Label>
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px',
                fontStyle: 'italic' 
              }}>
                Override default Open Graph tags for social media sharing. Leave fields empty to use default values from index.html.
              </div>
            </div>
            <div style={{
              fontSize: '20px',
              color: theme.colors.doryBlue,
              fontWeight: 'bold',
              userSelect: 'none'
            }}>
              {isOgSectionExpanded ? '−' : '+'}
            </div>
          </div>

          {isOgSectionExpanded && (
            <div style={{
              marginTop: theme.spacing.md,
              padding: theme.spacing.md,
              backgroundColor: '#fafafa',
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.doryLighterBlue}`
            }}>
              <FormGroup>
            <Label>og:title</Label>
            <Input
              type="text"
              value={formData.content?.ogMetaTags?.title || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  title: e.target.value
                }, 'content');
              }}
              placeholder="Page title for social sharing"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The title that appears when sharing on social media (e.g., Facebook, Twitter, LinkedIn)
            </div>
          </FormGroup>

          <FormGroup>
            <Label>og:description</Label>
            <TextArea
              value={formData.content?.ogMetaTags?.description || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  description: e.target.value
                }, 'content');
              }}
              placeholder="Page description for social sharing"
              rows={3}
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              A brief description of the page content (typically 150-200 characters)
            </div>
          </FormGroup>

          <FormGroup>
            <Label>og:image</Label>
            <Input
              type="url"
              value={formData.content?.ogMetaTags?.image || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  image: e.target.value
                }, 'content');
              }}
              placeholder="https://example.com/image.jpg"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              URL to the image that appears when sharing. Recommended size: 1200x630 pixels.
            </div>
            {formData.content?.ogMetaTags?.image && (
              <div style={{ 
                marginTop: theme.spacing.sm,
                textAlign: 'center'
              }}>
                <img 
                  src={formData.content.ogMetaTags.image} 
                  alt="OG image preview"
                  style={{
                    maxWidth: '300px',
                    maxHeight: '157px',
                    objectFit: 'contain',
                    border: `2px solid ${theme.colors.doryLighterBlue}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.sm
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: theme.colors.textLight,
                  marginTop: theme.spacing.xs
                }}>
                  Preview
                </div>
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>og:image:width</Label>
            <Input
              type="number"
              value={formData.content?.ogMetaTags?.imageWidth || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  imageWidth: e.target.value
                }, 'content');
              }}
              placeholder="1200"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Width of the og:image in pixels (typically 1200)
            </div>
          </FormGroup>

          <FormGroup>
            <Label>og:image:height</Label>
            <Input
              type="number"
              value={formData.content?.ogMetaTags?.imageHeight || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  imageHeight: e.target.value
                }, 'content');
              }}
              placeholder="630"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              Height of the og:image in pixels (typically 630)
            </div>
          </FormGroup>

          <FormGroup>
            <Label>og:url</Label>
            <Input
              type="url"
              value={formData.content?.ogMetaTags?.url || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  url: e.target.value
                }, 'content');
              }}
              placeholder="https://entry.thrivewithdory.com/optimalpace/screen-key"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The canonical URL for this page
            </div>
          </FormGroup>

          <FormGroup>
            <Label>og:type</Label>
            <Input
              type="text"
              value={formData.content?.ogMetaTags?.type || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  type: e.target.value
                }, 'content');
              }}
              placeholder="website"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The type of content (typically "website" or "article")
            </div>
          </FormGroup>

          <FormGroup>
            <Label>og:site_name</Label>
            <Input
              type="text"
              value={formData.content?.ogMetaTags?.siteName || ''}
              onChange={(e) => {
                handleInputChange('ogMetaTags', {
                  ...formData.content?.ogMetaTags,
                  siteName: e.target.value
                }, 'content');
              }}
              placeholder="Optimal Pace Academy"
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '4px', 
              fontStyle: 'italic' 
            }}>
              The name of the website/site
            </div>
          </FormGroup>
            </div>
          )}
        </FormGroup>
      )}
    </FormSection>
  );

    const renderScreenCheckpointOptions = () => (
      <>
        <option value="">— End of funnel —</option>
        <optgroup label="Screens">
          {availableScreens.map((screen) => (
            <option key={screen.key} value={screen.key}>
              {screen.key} ({screen.type})
            </option>
          ))}
        </optgroup>
        <optgroup label="Checkpoints">
          {availableCheckpoints.map((checkpoint) => (
            <option key={checkpoint.key} value={checkpoint.key}>
              {checkpoint.key} ({checkpoint.type})
            </option>
          ))}
        </optgroup>
      </>
    );

    const renderNavigationFields = () => (
    <FormSection>
      <SectionTitle>Navigation</SectionTitle>
      
      {/* Conditional navigation for yes/no and commitment */}
      {type === 'checkpoint' &&
      (formData.type === 'yes_no' || formData.type === 'commitment') ? (
        <>
          <NextDestinationField
            label='Next for "Yes" Answer'
            value={formData.nextScreen?.conditions?.[1] || ''}
            onChange={(value) => handleYesNoNavigation('yes', value)}
            screenOptions={renderScreenCheckpointOptions()}
          />
          <NextDestinationField
            label='Next for "No" Answer'
            value={formData.nextScreen?.conditions?.[0] || ''}
            onChange={(value) => handleYesNoNavigation('no', value)}
            screenOptions={renderScreenCheckpointOptions()}
          />
          <NextDestinationField
            label="Default Next (Fallback)"
            value={formData.nextScreen?.default || ''}
            onChange={(value) => handleYesNoNavigation('default', value)}
            screenOptions={renderScreenCheckpointOptions()}
          />
        </>
      ) : (
        <NextDestinationField
          label="Next Screen / Checkpoint / URL"
          value={typeof formData.nextScreen === 'string' ? formData.nextScreen : ''}
          onChange={(value) => handleInputChange('nextScreen', value)}
          screenOptions={renderScreenCheckpointOptions()}
        />
      )}
    </FormSection>
  );

  const renderConversionFields = () => (
    <FormSection>
      <SectionTitle>Ad Conversion Tracking</SectionTitle>

      <FormGroup>
        <Label>
          <input
            type="checkbox"
            checked={formData.conversionConfig?.enabled || false}
            onChange={(e) => handleConversionConfigChange('enabled', e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Send conversion events for this {type === 'screen' ? 'screen' : 'checkpoint'}
        </Label>
      </FormGroup>

      {formData.conversionConfig?.enabled && (
        <>
          <FormGroup>
            <Label>When to send</Label>
            <Select
              value={formData.conversionConfig?.trigger || 'complete'}
              onChange={(e) => handleConversionConfigChange('trigger', e.target.value)}
            >
              <option value="complete">When completed</option>
              <option value="load">When loaded / viewed</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Conversion Value (optional)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.conversionConfig?.value ?? ''}
              onChange={(e) => handleConversionConfigChange('value', e.target.value)}
              placeholder="e.g. 49.00"
            />
          </FormGroup>

          <FormGroup>
            <Label>Currency</Label>
            <Input
              value={formData.conversionConfig?.currency || 'USD'}
              onChange={(e) => handleConversionConfigChange('currency', e.target.value.toUpperCase())}
              placeholder="USD"
            />
          </FormGroup>

          {type === 'checkpoint' && ['payment', 'payment_simple', 'payment_membership', 'marketing_landing_page'].includes(formData.type) && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.conversionConfig?.usePaymentAmount || false}
                  onChange={(e) => handleConversionConfigChange('usePaymentAmount', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Use payment amount from this checkpoint when available
              </Label>
            </FormGroup>
          )}

          <SectionTitle style={{ marginTop: theme.spacing.md, fontSize: theme.typography.sizes.bodyLarge }}>Meta</SectionTitle>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.conversionConfig?.meta?.enabled || false}
                onChange={(e) => handleConversionConfigChange('meta.enabled', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send Meta Conversions API event (requires fbclid)
            </Label>
          </FormGroup>
          {formData.conversionConfig?.meta?.enabled && (
            <>
              <FormGroup>
                <Label>Meta Event Name</Label>
                <Input
                  value={formData.conversionConfig?.meta?.eventName || ''}
                  onChange={(e) => handleConversionConfigChange('meta.eventName', e.target.value)}
                  placeholder="Lead, Purchase, CompleteRegistration, or custom name"
                />
              </FormGroup>
              <FormGroup>
                <Label>Meta Test Event Code (testing only)</Label>
                <Input
                  value={formData.conversionConfig?.meta?.testEventCode || ''}
                  onChange={(e) => handleConversionConfigChange('meta.testEventCode', e.target.value)}
                  placeholder="TEST12345 — from Events Manager > Test Events. Leave blank for live events."
                />
              </FormGroup>
            </>
          )}

          <SectionTitle style={{ marginTop: theme.spacing.md, fontSize: theme.typography.sizes.bodyLarge }}>LinkedIn</SectionTitle>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.conversionConfig?.linkedin?.enabled || false}
                onChange={(e) => handleConversionConfigChange('linkedin.enabled', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send LinkedIn Conversions API event (requires li_fat_id)
            </Label>
          </FormGroup>
          {formData.conversionConfig?.linkedin?.enabled && (
            <FormGroup>
              <Label>LinkedIn Conversion Rule URN</Label>
              <Input
                value={formData.conversionConfig?.linkedin?.conversionRuleUrn || ''}
                onChange={(e) => handleConversionConfigChange('linkedin.conversionRuleUrn', e.target.value)}
                placeholder="urn:lla:llaPartnerConversion:123456"
              />
            </FormGroup>
          )}

          <SectionTitle style={{ marginTop: theme.spacing.md, fontSize: theme.typography.sizes.bodyLarge }}>Google Ads</SectionTitle>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.conversionConfig?.google?.enabled || false}
                onChange={(e) => handleConversionConfigChange('google.enabled', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send Google Ads conversion (requires gclid, gbraid, or wbraid)
            </Label>
          </FormGroup>
          {formData.conversionConfig?.google?.enabled && (
            <>
              <FormGroup>
                <Label>Google Conversion Action ID</Label>
                <Input
                  value={formData.conversionConfig?.google?.conversionActionId || ''}
                  onChange={(e) => handleConversionConfigChange('google.conversionActionId', e.target.value)}
                  placeholder="1234567890"
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.conversionConfig?.google?.validateOnly || false}
                    onChange={(e) => handleConversionConfigChange('google.validateOnly', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Validate only (testing) — submits the full payload to Google for validation without recording a conversion
                </Label>
              </FormGroup>
            </>
          )}
        </>
      )}
    </FormSection>
  );

  return (
    <Container>
      <Header>
        <Title>
          {screen ? 'Edit' : 'Create'} {type === 'screen' ? 'Screen' : 'Checkpoint'}
        </Title>
        <HeaderActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSaveOnly} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="primary" onClick={handleSaveAndClose} disabled={loading}>
            {loading ? 'Saving...' : 'Save & Close'}
          </Button>
        </HeaderActions>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <FormCard>
        <FormGrid>
          {renderBasicFields()}
          {renderNavigationFields()}
        </FormGrid>

        {renderConversionFields()}
        
        {renderContentFields()}
      </FormCard>
    </Container>
  );
};

export default ScreenEditor; 